/* =====================================================================
   Wildora Personalization Preview — rendering engine (v2.2, caption mode)
   Modes:
     pocket-name    — single name field (blankets, bags, sleepwear, etc.)
     custom-caption — caption with balanced two-line wrap centered on the
                      printed baseline (tees, crop tops)
     name-garden / name-mountain — crop-top compositions from a typed name
   Canvas space: 1000x1000 normalized frame, per the calibration configs.

   v2.5 (Aug 20 2026): per-config left anchoring for single-line text.
     placement.align:'left' starts the run at placement.anchorX and grows
     rightward instead of centring on centerX. Added for the dog bandanas,
     whose print sits near the fabric's left edge. Configs without the key are
     unaffected.

   v2.4 (Aug 19 2026): damp the collar curve for looks — CURVE_DAMP on the rise
     from the apex, ROT_DAMP on the tilt. Geometry was correct but read as
     over-curved without perspective foreshortening. Fit unchanged.
     CURVE_DAMP raised 0.6 -> 0.75 after review: at 0.6 the line was flatter
     than the strap and long names rode high in the band at the ends.
     Rotation was the part that read wrong, so ROT_DAMP stays at 0.4.
     CURVE_DAMP is overridable per config via placement.curve.curveDamp;
     ROT_DAMP is not, by design.

   v2.3 (Aug 19 2026): curved single-line text for the dog collars. Glyphs
     are placed along a quadratic fitted to the strap's outer bottom edge and
     rotated to the local slope, stepping by arc length. Opt-in per config via
     placement.curve — every other product renders exactly as before.

   v2.2 (Jul 30 2026): fix custom-caption never wrapping to two lines.
     splitBalanced() was being handed placement.maxWidth as its wrap trigger.
     maxWidth is the per-line SHRINK-TO-FIT CEILING applied AFTER wrapping
     (420 in 1000-space for the Secret Garden tee) — it is not the point at
     which a caption should break. The calibrated trigger, per the approved
     prototype, is refWidth * 1.25: wrap once the caption runs meaningfully
     longer than the reference line it was calibrated against.
     "Meet Me In The Secret Garden" measures ~388 in 1000-space, so under the
     old trigger (<= 420) it always returned a single line — on every viewport,
     desktop included. Against the correct trigger (233.75) it wraps.
     Falls back to maxWidth when a config carries no refWidth, so configs
     without one behave exactly as before. No config values changed.

     NOTE: measurement was already scale-independent. setFont() sets
     (px * s) and widthOf() divides measureText() by the same s, so every
     width is in 1000-space regardless of canvas size or devicePixelRatio.
     Verified 387.46–387.57 (0.03% spread) across dpr 1–3 and canvas widths
     320–413. Do not "fix" that; it is not where the bug was.

   v2.1 (Jul 15 2026): preload the Est.-line font before first render.
   ===================================================================== */
(function () {
  'use strict';
  var FRAME = 1000;
  /* Aesthetic damping for the curved collar text. The fit itself is faithful —
     traced stitching on la-vie-fleurie drops ~31px over 200px from centre
     against the fit's 26px, so if anything the curve is slightly under. But
     pure rotation without perspective foreshortening reads as warped, so both
     the rise and the tilt are pulled back at render time. These are taste
     values, not a correction to the coefficients: do NOT refit a, b or c to
     compensate for them. Expect to tune these again. */
  var CURVE_DAMP = 0.75;  /* share of the curve's rise from its apex that is kept */
  var ROT_DAMP   = 0.4;   /* share of the local tilt that is kept */
  /* Caption wraps once it exceeds this multiple of the calibrated reference
     line width (placement.refWidth). Matches the approved prototype. */
  var WRAP_FACTOR = 1.25;

  function WildoraPreview(canvas, config) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.config = config;
    this.blank = null;
    this.text = '';
    this.lines = [];
    this.ready = false;
  }

  WildoraPreview.prototype.load = function () {
    var self = this, cfg = self.config;
    var fontFamily = cfg.fontFamily || 'Cookie';
    var estFontFamily = cfg.estFontFamily || 'Cookie';
    var imgP = cfg.blankUrl ? new Promise(function (res, rej) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { self.blank = img; res(); };
      img.onerror = function () { res(); };
      img.src = cfg.blankUrl;
    }) : Promise.resolve();
    var fontP = (document.fonts && document.fonts.load)
      ? Promise.all([
          document.fonts.load('64px "' + fontFamily + '"'),
          document.fonts.load('64px "' + estFontFamily + '"')
        ]).then(function () { return document.fonts.ready; })
         .catch(function () {})
      : Promise.resolve();
    return Promise.all([imgP, fontP]).then(function () { self.ready = true; self.render(); });
  };

  WildoraPreview.prototype.setText = function (t) {
    this.text = (t || '').trim();
    if (this.ready) this.render();
  };
  WildoraPreview.prototype.setName = WildoraPreview.prototype.setText;
  WildoraPreview.prototype.getLines = function () { return this.lines.slice(); };

  function splitBalanced(ctx, text, maxW) {
    if (ctx.measureText(text).width <= maxW) return [text];
    var words = text.split(/\s+/);
    if (words.length < 2) return [text];
    var best = null, bestDiff = Infinity;
    for (var i = 1; i < words.length; i++) {
      var a = words.slice(0, i).join(' ');
      var b = words.slice(i).join(' ');
      var wa = ctx.measureText(a).width, wb = ctx.measureText(b).width;
      var diff = Math.abs(wa - wb);
      if (diff < bestDiff) { bestDiff = diff; best = [a, b]; }
    }
    return best;
  }

  /* --- curved single-line text (dog collars) -------------------------------
     A collar is photographed as a loop, so the strap curves away at both ends
     and a straight baseline runs off the webbing on long names. maxWidth
     shrinking alone does not fix it — the text gets smaller and still bleeds.
     Glyphs are placed individually along a quadratic fitted to the collar's
     outer bottom edge, rotated to the local slope:
         edgeY(x) = a*x^2 + b*x + c
         baseY(x) = edgeY(x) - offset
         angle(x) = atan(2*a*x + b)
     Stepping is by ARC LENGTH, not by x. Stepping in x alone visibly
     compresses letters toward the ends where the curve is steep.
     Gated on placement.curve being present, which only the dog collar configs
     carry — leashes and bandanas keep the straight renderer their calibration
     assumes. The fit is only valid between xMin and xMax (the front face of
     the strap), so x is clamped there. */
  function curveFns(cv, cfg) {
    var a = cv.a, b = cv.b, c = cv.c, off = cv.offset, ny = (cfg.nudgeY || 0);
    /* Per-config curve damping. The global CURVE_DAMP is the default; a config
       may override it with placement.curve.curveDamp when its strap does not
       tolerate the standard flattening. ROT_DAMP is deliberately NOT
       overridable — letters should stay upright by the same amount everywhere. */
    var cdamp = (typeof cv.curveDamp === 'number') ? cv.curveDamp : CURVE_DAMP;
    function edgeY(x) { return a * x * x + b * x + c; }
    /* Damp the deviation from the curve's own apex, not edgeY itself. Scaling
       edgeY would shift the whole line vertically as well as flatten it;
       damping about the apex flattens while holding the height at centre, so
       baseY(centerX) still reproduces the stored baseline. */
    var apexX = -b / (2 * a);
    var apexY = edgeY(apexX);
    return {
      baseY: function (x) { return apexY - (apexY - edgeY(x)) * cdamp - off + ny; },
      angle: function (x) { return Math.atan(2 * a * x + b) * ROT_DAMP; },
      xMin: cv.xMin, xMax: cv.xMax
    };
  }

  /* Walk the curve from x0 until the accumulated arc length reaches target.
     Negative target walks left. 0.5px steps in x — the brief calls for no
     better than ~0.5px and finer steps buy nothing visible. */
  function xAtArc(f, x0, target) {
    var STEP = 0.5;
    var dir = target < 0 ? -1 : 1;
    var remaining = Math.abs(target);
    var x = x0;
    while (remaining > 0) {
      var nx = x + dir * STEP;
      if (nx < f.xMin || nx > f.xMax) { x = nx < f.xMin ? f.xMin : f.xMax; break; }
      var dy = f.baseY(nx) - f.baseY(x);
      var ds = Math.sqrt(STEP * STEP + dy * dy);
      if (ds >= remaining) { x = x + dir * STEP * (remaining / ds); break; }
      remaining -= ds;
      x = nx;
    }
    return Math.max(f.xMin, Math.min(f.xMax, x));
  }

  WildoraPreview.prototype.drawCurvedLine = function (ctx, text, cfg, s, widthOf) {
    var f = curveFns(cfg.placement.curve, cfg);
    var cx = cfg.placement.centerX + (cfg.nudgeX || 0);
    var total = widthOf(text);
    var acc = 0;
    for (var i = 0; i < text.length; i++) {
      var ch = text.charAt(i);
      var adv = widthOf(ch);
      var mid = acc + adv / 2 - total / 2;   /* arc offset of this glyph centre */
      var gx = xAtArc(f, cx, mid);
      ctx.save();
      ctx.translate(gx * s, f.baseY(gx) * s);
      ctx.rotate(f.angle(gx));
      ctx.fillText(ch, 0, 0);                /* textAlign is centre */
      ctx.restore();
      acc += adv;
    }
  };

  WildoraPreview.prototype.render = function () {
    var c = this.canvas, ctx = this.ctx, cfg = this.config;
    var cssW = c.clientWidth || c.width;
    var dpr = window.devicePixelRatio || 1;
    if (c.width !== Math.round(cssW * dpr)) {
      c.width = Math.round(cssW * dpr);
      c.height = Math.round(cssW * dpr);
    }
    var s = c.width / FRAME;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    if (this.blank) ctx.drawImage(this.blank, 0, 0, c.width, c.height);
    this.lines = [];
    if (!this.text) return;

    var fontFamily = cfg.fontFamily || 'Cookie';
    var estFontFamily = cfg.estFontFamily || 'Cookie';
    var size = cfg.baseFontSize * (cfg.sizeScale || 1);
    var maxW = cfg.placement.maxWidth;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'center';

    function setFont(px) { ctx.font = (px * s) + 'px "' + fontFamily + '", cursive'; }
    function widthOf(t) { return ctx.measureText(t).width / s; }

    var lines, baselines;
    var estDrop = 0;
    if (cfg.mode === 'name-garden' || cfg.mode === 'name-mountain') {
      var name = this.text.replace(/\s+$/, '');
      var apostrophe = '’';
      if (cfg.mode === 'name-garden') {
        var headline = name + apostrophe + 's Garden';
        setFont(size);
        var hw = widthOf(headline);
        if (hw > maxW) { size = size * (maxW / hw); setFont(size); }
        ctx.save();
        ctx.fillStyle = cfg.inkColor;
        ctx.fillText(headline,
          (cfg.placement.centerX + (cfg.nudgeX || 0)) * s,
          (cfg.placement.baseline + (cfg.nudgeY || 0)) * s);
        if (cfg.showEst2026 && cfg.placement.est) {
          var est = cfg.placement.est;
          ctx.font = ((cfg.estFontSize || 32) * s) + 'px "' + estFontFamily + '", cursive';
          ctx.fillText(est.text || 'Est. 2026',
            ((est.cx || est.x) + (cfg.nudgeX || 0)) * s,
            ((est.baseline || est.y) + (cfg.nudgeY || 0)) * s);
        }
        this.finishFabric(ctx, c, cfg);
        ctx.restore();
        this.lines = [headline, 'Est. 2026'];
        return;
      } else {
        var line1 = name + apostrophe + 's';
        setFont(size);
        var w1 = widthOf(line1);
        if (w1 > maxW) { size = size * (maxW / w1); setFont(size); }
        ctx.save();
        ctx.fillStyle = cfg.inkColor;
        ctx.fillText(line1,
          (cfg.placement.centerX + (cfg.nudgeX || 0)) * s,
          (cfg.placement.baseline + (cfg.nudgeY || 0)) * s);
        var L2 = cfg.placement.line2;
        ctx.font = ((cfg.line2FontSize || size) * s) + 'px "' + fontFamily + '", cursive';
        ctx.fillText(L2.text,
          (L2.cx + (cfg.nudgeX || 0)) * s,
          (L2.baseline + (cfg.nudgeY || 0)) * s);
        this.finishFabric(ctx, c, cfg);
        ctx.restore();
        this.lines = [line1, L2.text];
        return;
      }
    }
    if (cfg.mode === 'custom-caption') {
      setFont(size);
      /* v2.2: wrap trigger is NOT the shrink-to-fit ceiling. Break the caption
         once it runs meaningfully longer than the calibrated reference line;
         maxWidth stays the per-line ceiling enforced after the split below.
         Both values are 1000-space, and widthOf() returns 1000-space, so this
         decision is identical on every canvas size and devicePixelRatio. */
      var wrapAt = cfg.placement.refWidth
        ? cfg.placement.refWidth * WRAP_FACTOR
        : maxW;
      lines = splitBalanced({ measureText: function(t){ return { width: widthOf(t) }; } },
                            this.text, wrapAt);
      var widest = Math.max.apply(null, lines.map(widthOf));
      if (widest > maxW) { size = size * (maxW / widest); setFont(size); }
      var base = cfg.placement.singleBaseline;
      var gap  = (cfg.placement.lineGap || 40) * (cfg.lineSpacing || 1);
      if (lines.length === 1) {
        baselines = [base];
      } else {
        size = size * (cfg.twoLineScale || 0.85);
        setFont(size);
        var widest2 = Math.max.apply(null, lines.map(widthOf));
        if (widest2 > maxW) { size = size * (maxW / widest2); setFont(size); }
        estDrop = cfg.twoLineDrop || 0;
        baselines = [base + estDrop - gap, base + estDrop];
      }
    } else {
      setFont(size);
      var w = widthOf(this.text);
      if (w > maxW) { size = size * (maxW / w); setFont(size); }
      lines = [this.text];
      baselines = [cfg.placement.baseline];
    }

    var x = (cfg.placement.centerX + (cfg.nudgeX || 0)) * s;
    var curved = cfg.mode === 'pocket-name' && cfg.placement && cfg.placement.curve;
    /* Left-anchored runs (the dog bandanas). Centred text grows both ways,
       which is wrong when the print sits near one edge: on a bandana the
       fabric's left edge is ~100px from the name's start but ~570px of empty
       square lies to the right, so a long name runs off the LEFT while the
       right side goes unused. With align:'left' the run starts at anchorX and
       grows rightward into the space that is actually there.
       anchorX supersedes centerX only when align is 'left'; centerX stays in
       the configs as a fallback. The two are close by construction —
       anchorX ~= centerX - refWidth/2 — because the old centred position put
       the reference string's left edge in roughly the same place.
       textAlign is part of the saved canvas state, so the ctx.save() above
       scopes this to the run. Leash and collar configs carry no align key and
       take the centred path unchanged. */
    var leftAlign = cfg.placement && cfg.placement.align === 'left';
    ctx.save();
    ctx.fillStyle = cfg.inkColor;
    if (leftAlign && !curved) {
      ctx.textAlign = 'left';
      var ax = (cfg.placement.anchorX != null) ? cfg.placement.anchorX : cfg.placement.centerX;
      x = (ax + (cfg.nudgeX || 0)) * s;
    }
    for (var i = 0; i < lines.length; i++) {
      if (curved) this.drawCurvedLine(ctx, lines[i], cfg, s, widthOf);
      else ctx.fillText(lines[i], x, (baselines[i] + (cfg.nudgeY || 0)) * s);
    }
    if (cfg.showEst2026 && cfg.placement.est) {
      var estS = cfg.placement.est;
      ctx.font = ((cfg.estFontSize || 32) * s) + 'px "' + estFontFamily + '", cursive';
      ctx.fillText(estS.text || 'Est. 2026',
        ((estS.cx || estS.x) + (cfg.nudgeX || 0)) * s,
        ((estS.baseline || estS.y) + estDrop + (cfg.nudgeY || 0)) * s);
    }
    this.finishFabric(ctx, c, cfg);
    ctx.restore();
    this.lines = lines;
  };

  WildoraPreview.prototype.finishFabric = function (ctx, c, cfg) {
    if (this.blank && cfg.fabricBlend > 0) {
      ctx.globalCompositeOperation = 'source-atop';
      ctx.globalAlpha = cfg.fabricBlend;
      ctx.drawImage(this.blank, 0, 0, c.width, c.height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
    }
  };

  window.WildoraPreview = WildoraPreview;
})();
